<template>
  <div class="page-header-wrapper">
    <div class="page-header">
      <div class="header-content">
        <!-- Back Button -->
        <button 
          v-if="showBackButton" 
          @click="goBack" 
          class="back-btn"
          v-tooltip="'Go back'"
        >
          <vue-feather type="arrow-left" size="18" />
        </button>

        <!-- Icon -->
        <div v-if="icon" class="page-icon">
          <vue-feather :type="icon" size="20" />
        </div>

        <!-- Title & Breadcrumb -->
        <div class="title-section">
          <div class="title-row">
            <h1 class="page-title">{{ currentTitle }}</h1>
            <span v-if="badge" :class="['title-badge', badgeVariant]">{{ badge }}</span>
          </div>
          
          <!-- Breadcrumb Trail -->
          <nav class="breadcrumb-nav" v-if="items.length > 1" aria-label="Breadcrumb">
            <ol class="breadcrumb-list">
              <li v-for="(item, index) in breadcrumbItems" :key="index" class="breadcrumb-item">
                <router-link 
                  v-if="item.url && !item.active" 
                  :to="item.url" 
                  class="breadcrumb-link"
                  :class="{ 'home-link': index === 0 }"
                >
                  <vue-feather v-if="index === 0" type="home" size="12" class="home-icon" />
                  <span>{{ item.title }}</span>
                </router-link>
                <span v-else class="breadcrumb-current" :class="{ 'active': item.active }">
                  {{ item.title }}
                </span>
                <vue-feather 
                  v-if="index < breadcrumbItems.length - 1" 
                  type="chevron-right" 
                  size="12" 
                  class="breadcrumb-separator" 
                />
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <!-- Actions -->
      <div class="header-actions">
        <slot name="actions"></slot>
      </div>
    </div>

    <!-- Optional Description -->
    <p v-if="description" class="page-description">{{ description }}</p>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'BreadcrumbIndex',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    icon: {
      type: String,
      default: null
    },
    badge: {
      type: String,
      default: null
    },
    badgeVariant: {
      type: String,
      default: 'badge-primary' // badge-primary, badge-success, badge-warning, badge-danger
    },
    description: {
      type: String,
      default: null
    },
    showBack: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const router = useRouter()

    const currentTitle = computed(() => {
      const activeItem = props.items.find(item => item.active)
      return activeItem?.title || props.items[props.items.length - 1]?.title || ''
    })

    const breadcrumbItems = computed(() => {
      return props.items.map((item, index) => ({
        ...item,
        active: index === props.items.length - 1 || item.active
      }))
    })

    const showBackButton = computed(() => {
      return props.showBack || props.items.length > 2
    })

    const goBack = () => {
      const prevItem = props.items[props.items.length - 2]
      if (prevItem?.url) {
        router.push(prevItem.url)
      } else {
        router.back()
      }
    }

    return { 
      currentTitle, 
      breadcrumbItems,
      showBackButton,
      goBack
    }
  }
}
</script>

<style scoped>
/* App Color Variables */
:root {
  --primary: #003049;
  --primary-light: #073b4c;
  --secondary: #8ecae6;
  --accent: #fcbf49;
  --accent-alt: #ff595e;
  --success: #22c55e;
  --text-color: #353535;
  --text-muted: #6b7280;
  --border-color: #d9d9d9;
}

.page-header-wrapper {
  margin-bottom: 24px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 48px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.back-btn:hover {
  background: #f8f9fa;
  color: #003049;
  border-color: #003049;
}

.back-btn:active {
  transform: scale(0.95);
}

.page-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  border-radius: 10px;
  color: #fff;
  flex-shrink: 0;
}

.title-section {
  min-width: 0;
  flex: 1;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #353535;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.title-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-primary {
  background: rgba(0, 48, 73, 0.1);
  color: #003049;
}

.badge-success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.badge-warning {
  background: rgba(252, 191, 73, 0.15);
  color: #b45309;
}

.badge-danger {
  background: rgba(255, 89, 94, 0.1);
  color: #ff595e;
}

.badge-info {
  background: rgba(142, 202, 230, 0.2);
  color: #0369a1;
}

/* Breadcrumb Styles */
.breadcrumb-nav {
  margin-top: 4px;
}

.breadcrumb-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 2px;
}

.breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.breadcrumb-link:hover {
  color: #003049;
  background: rgba(0, 48, 73, 0.08);
}

.breadcrumb-link.home-link {
  padding: 2px 4px;
}

.home-icon {
  opacity: 0.7;
}

.breadcrumb-current {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  padding: 2px 6px;
}

.breadcrumb-current.active {
  color: #003049;
  background: rgba(0, 48, 73, 0.08);
  border-radius: 4px;
}

.breadcrumb-separator {
  color: #d9d9d9;
  flex-shrink: 0;
  margin: 0 2px;
}

/* Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Description */
.page-description {
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  padding-left: 52px;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .header-content {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .page-title {
    font-size: 1.125rem;
  }

  .page-icon {
    width: 36px;
    height: 36px;
  }

  .back-btn {
    width: 32px;
    height: 32px;
  }

  .breadcrumb-list {
    max-width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .breadcrumb-list::-webkit-scrollbar {
    display: none;
  }

  .page-description {
    padding-left: 0;
  }
}

@media (max-width: 480px) {
  .page-icon {
    display: none;
  }

  .breadcrumb-link,
  .breadcrumb-current {
    font-size: 11px;
    padding: 2px 4px;
  }
}

/* Dark Mode */
:global(body.dark-mode) .page-title {
  color: #f3f4f6;
}

:global(body.dark-mode) .back-btn {
  background: #1f2937;
  border-color: #374151;
  color: #9ca3af;
}

:global(body.dark-mode) .back-btn:hover {
  background: #374151;
  color: #f3f4f6;
  border-color: #4b5563;
}

:global(body.dark-mode) .page-icon {
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
}

:global(body.dark-mode) .breadcrumb-link {
  color: #9ca3af;
}

:global(body.dark-mode) .breadcrumb-link:hover {
  color: #8ecae6;
  background: rgba(142, 202, 230, 0.1);
}

:global(body.dark-mode) .breadcrumb-current {
  color: #6b7280;
}

:global(body.dark-mode) .breadcrumb-current.active {
  color: #8ecae6;
  background: rgba(142, 202, 230, 0.1);
}

:global(body.dark-mode) .breadcrumb-separator {
  color: #4b5563;
}

:global(body.dark-mode) .page-description {
  color: #9ca3af;
}

:global(body.dark-mode) .badge-primary {
  background: rgba(142, 202, 230, 0.15);
  color: #8ecae6;
}

:global(body.dark-mode) .badge-success {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

:global(body.dark-mode) .badge-warning {
  background: rgba(252, 191, 73, 0.15);
  color: #fcbf49;
}

:global(body.dark-mode) .badge-danger {
  background: rgba(255, 89, 94, 0.15);
  color: #ff595e;
}

:global(body.dark-mode) .badge-info {
  background: rgba(142, 202, 230, 0.15);
  color: #8ecae6;
}
</style>
